import { useMemo } from "react";
import { Team } from "../../types/team";
import { SYSTEM_LANGUAGE_LABELS } from "../../shared/enums/shared-enums";
import { SystemLanguages } from "../../shared/enums/shared-enums";
import {
  chineseTeamColumns,
  tibetanTeamColumns,
} from "../../common/entities/TeamColumns";
import { DEFAULT_INTELLEXUS_DATE } from "../../types/intellexusDate";
import { initMetadata } from "../../controllers/formController";
import { SystemEntityProofreadingStatus } from "../../shared/enums/shared-enums";
import { MRT_ColumnDef } from "material-react-table";

const SOURCE_LANGUAGE_ENUM_MAP: Partial<Record<SystemLanguages, string>> = {
  [SystemLanguages.tibetan]: "tibetan_source_language",
  [SystemLanguages.chinese]: "chinese_source_language",
} as const;

const useTeamLogic = (language: string | null | undefined) => {
  const systemLanguageType = useMemo<
    Team["system_language_type"] | null
  >(() => {
    if (
      !Object.values(SystemLanguages).includes(language as SystemLanguages) ||
      (language !== SystemLanguages.tibetan &&
        language !== SystemLanguages.chinese)
    ) {
      return null;
    }
    return language as Team["system_language_type"];
  }, [language]);

  const languageBasedColumns = useMemo<MRT_ColumnDef<Team, any>[]>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.tibetan:
        return tibetanTeamColumns;
      case SystemLanguages.chinese:
        return chineseTeamColumns;
    }
  }, [systemLanguageType]);

  const sourceLanguageEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return SOURCE_LANGUAGE_ENUM_MAP[systemLanguageType] || "";
  }, [systemLanguageType]);

  const placeIdentificationStatusEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return "team_contribution_place_identification_status";
  }, [systemLanguageType]);

  const revisionEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return "revision";
  }, [systemLanguageType]);

  const userEnums = useMemo<string[]>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.chinese:
        return [sourceLanguageEnum, placeIdentificationStatusEnum];
      case SystemLanguages.tibetan:
        return [
          sourceLanguageEnum,
          placeIdentificationStatusEnum,
          revisionEnum,
        ];
    }
  }, [
    systemLanguageType,
    sourceLanguageEnum,
    placeIdentificationStatusEnum,
    revisionEnum,
  ]);

  const metadataFieldNames = useMemo<Array<keyof Team>>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.tibetan:
        return [
          "source_language",
          "team_contribution_date",
          "place_identification_status",
          "place",
          "version",
          "revision",
        ];
      case SystemLanguages.chinese:
        return [
          "source_language",
          "team_contribution_date",
          "title",
          "taisho_catalog_no",
          "place_identification_status",
          "place",
        ];
    }
  }, [systemLanguageType]);

  const languageBasedFields = useMemo<Array<keyof Team>>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.tibetan:
        return ["revision", "version"];
      case SystemLanguages.chinese:
        return ["title", "taisho_catalog_no"];
    }
  }, [systemLanguageType]);

  const showField = (field: keyof Team): boolean => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return languageBasedFields.includes(field);
  };

  const defaultTeam = useMemo<Team>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return {
      _id: "",
      _key: "",
      _rev: "",
      name: "",
      notes: "",
      published_id: "",
      source_language: null,
      team_contribution_date: DEFAULT_INTELLEXUS_DATE,
      version: null,
      place: null,
      revision: null,
      title: "",
      taisho_catalog_no: "",
      place_identification_status: null,
      place_id: "",
      version_id: null,
      system_language_type: systemLanguageType,
      attributes_metadata: initMetadata<Team>(metadataFieldNames),
      internal_notes: "",
      entity_status_of_proofreading:
        SystemEntityProofreadingStatus.NOT_PROOFREAD,
    };
  }, [systemLanguageType, metadataFieldNames]);

  const getDisplayName = (team?: Team | null): string => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    const systemLanguageLabel = SYSTEM_LANGUAGE_LABELS[systemLanguageType];
    const prefix = systemLanguageLabel ? `${systemLanguageLabel} Team` : "Team";
    const displayName = !team
      ? ""
      : (team.name || team.published_id ? `: ` : "") +
        team.published_id +
        (team.published_id && team.name ? " - " : "") +
        team.name;
    return `${prefix}${displayName}`;
  };

  return {
    systemLanguageType,
    languageBasedColumns,
    userEnums,
    metadataFieldNames,
    defaultTeam,
    showField,
    getDisplayName,
    sourceLanguageEnum,
    placeIdentificationStatusEnum,
    revisionEnum,
  };
};

export default useTeamLogic;
